/**
 * Review Center API Client
 */
import { apiClient } from "@/lib/api-client";

// ============================================================================
// Types
// ============================================================================

export interface ReviewItem {
    review_item_id: string;
    company_id: string;
    container_id: string | null;
    review_type:
        | "container_identification"
        | "document_assignment"
        | "tracking_staleness"
        | "missing_initial_docs"
        | "missing_documents";
    status: "pending" | "resolved" | "dismissed" | "expired";
    confidence_score: number | null;
    source: string;
    context: Record<string, unknown> | null;
    resolution: Record<string, unknown> | null;
    resolved_by: string | null;
    resolved_at: string | null;
    created_at: string | null;
    updated_at: string | null;
    expires_at: string | null;
}

export interface ExtractedReferences {
    booking_numbers: string[];
    container_numbers: string[];
    bl_numbers: string[];
    other: string[];
}

export interface SystemAction {
    type: string;
    title: string;
    timestamp: string | null;
    repeat_count?: number;
    source?: string;
    description?: string;
    changes?: Record<string, string>;
    issue_type?: string;
    severity?: string;
    urgency?: string;
    email_subject?: string;
    email_from?: string;
}

export interface AgentQuestionOption {
    id: string;
    label: string;
    action: string;
}

export interface AgentQuestion {
    id: string;
    type: "single_choice" | "yes_no";
    question: string;
    options: AgentQuestionOption[];
}

export interface SituationContext {
    email_subject: string | null;
    email_from: string | null;
    email_received_at: string | null;
    email_summary: string | null;
    attention_reasons: string[];
    extracted_references: ExtractedReferences | null;
    container_creation: { created: boolean; container_number_found: string | null; attachments_count: number } | null;
    system_actions: SystemAction[];
    // EveMasterContainer agent analysis
    agent_analysis: string | null;
    agent_reasoning: string | null;
    agent_decision: string | null;
    questions: AgentQuestion[] | null;
    jsoncargo_result: Record<string, unknown> | null;
}

export interface ReviewItemDetail {
    review_item: ReviewItem;
    container: ReviewContainer | null;
    documents: ReviewDocument[];
    duplicate_suspects: ReviewContainer[];
    source_email_subject: string | null;
    situation_context: SituationContext | null;
}

export interface ReviewContainer {
    container_id: string;
    container_number: string | null;
    booking_number: string | null;
    bl_number: string | null;
    status: string;
    carrier_name: string | null;
    vessel_name: string | null;
    voyage_number: string | null;
    risk_score: number | null;
    created_at: string | null;
}

export interface ReviewDocument {
    document_id: string;
    container_id: string | null;
    document_type: string | null;
    original_filename: string | null;
    extraction_status: string | null;
    created_at: string | null;
}

export interface SourceHint {
    type: "email" | "document";
    id: string;
    label: string;
}

export interface ReviewContainerSummary {
    container_id: string;
    container_number: string | null;
    booking_number: string | null;
    status: string | null;
    pending_review_count: number;
    review_types: string[];
    lowest_confidence: number | null;
    created_at: string | null;
    document_count: number;
    source_hints: SourceHint[];
}

export interface ResolveRequest {
    confirmed?: boolean;
    corrections?: Record<string, string | null>;
    merge_with_container_id?: string;
    field_choices?: Record<string, string>;
    delete_container?: boolean;
    document_decisions?: Array<{
        document_id: string;
        action: "keep" | "reclassify" | "remove" | "move";
        new_type?: string;
        move_to_container_id?: string;
    }>;
    additional_containers?: Array<{
        booking_number?: string;
        container_number?: string;
        bl_number?: string;
        phase?: string;
    }>;
    question_answers?: Record<string, string>;
    notes?: string;
}

export interface MergeRequest {
    source_id: string;
    target_id: string;
    field_choices: Record<string, string>;
}

// ============================================================================
// API Functions
// ============================================================================

export async function fetchReviewContainers(params?: {
    skip?: number;
    limit?: number;
    include_resolved?: boolean;
}): Promise<{ containers: ReviewContainerSummary[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params?.skip) searchParams.set("skip", String(params.skip));
    if (params?.limit) searchParams.set("limit", String(params.limit));
    if (params?.include_resolved) searchParams.set("include_resolved", "true");
    const qs = searchParams.toString();
    return apiClient(`/review-center/containers${qs ? `?${qs}` : ""}`);
}

export async function fetchReviewItems(params?: {
    status?: string;
    review_type?: string;
    container_id?: string;
    search?: string;
    skip?: number;
    limit?: number;
}): Promise<{ items: ReviewItem[]; total: number }> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set("status", params.status);
    if (params?.review_type) searchParams.set("review_type", params.review_type);
    if (params?.container_id) searchParams.set("container_id", params.container_id);
    if (params?.search) searchParams.set("search", params.search);
    if (params?.skip) searchParams.set("skip", String(params.skip));
    if (params?.limit) searchParams.set("limit", String(params.limit));
    const qs = searchParams.toString();
    return apiClient(`/review-center/items${qs ? `?${qs}` : ""}`);
}

export async function fetchReviewItem(id: string): Promise<ReviewItemDetail> {
    return apiClient(`/review-center/items/${id}`);
}

export async function resolveReviewItem(
    id: string,
    data: ResolveRequest
): Promise<{ status: string; review_item: ReviewItem }> {
    return apiClient(`/review-center/items/${id}/resolve`, {
        method: "POST",
        body: JSON.stringify(data),
    });
}

export async function dismissReviewItem(
    id: string,
    reason?: string
): Promise<{ status: string; review_item: ReviewItem }> {
    return apiClient(`/review-center/items/${id}/dismiss`, {
        method: "POST",
        body: JSON.stringify({ reason }),
    });
}

export async function fetchHasPending(): Promise<{ has_pending: boolean }> {
    return apiClient("/review-center/has-pending");
}

export async function mergeContainers(
    data: MergeRequest
): Promise<{ status: string; container: ReviewContainer }> {
    return apiClient("/review-center/merge", {
        method: "POST",
        body: JSON.stringify(data),
    });
}
